/**
 * Safe service worker registration: shell/offline baseline only.
 * Does not cache authenticated API traffic.
 */

import { dispatchSwUpdateAvailable } from './swEvents';

const PUBLIC = process.env.PUBLIC_URL ?? '';
const SW_URL = `${PUBLIC}/service-worker.js`.replace(/([^:]\/)\/+/g, '$1');

function getWaitingWorker(reg: ServiceWorkerRegistration | null): ServiceWorker | null {
  return reg?.waiting ?? null;
}

/**
 * Register in production only. Resolves when registration is available (or null in dev / unsupported).
 */
export function registerServiceWorker(onUpdateAvailable?: () => void): Promise<ServiceWorkerRegistration | null> {
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }

  const scope = PUBLIC ? `${PUBLIC}/`.replace(/([^:]\/)\/+/g, '$1') : '/';

  return navigator.serviceWorker
    .register(SW_URL, { scope })
    .then((registration) => {
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            dispatchSwUpdateAvailable();
            onUpdateAvailable?.();
          }
        });
      });

      if (getWaitingWorker(registration)) {
        dispatchSwUpdateAvailable();
        onUpdateAvailable?.();
      }

      return registration;
    })
    .catch(() => null);
}

/** Tell the waiting service worker to activate; page reloads once on controllerchange. */
export function skipWaitingAndReload(): void {
  if (!navigator.serviceWorker) return;
  let reloaded = false;
  navigator.serviceWorker.addEventListener(
    'controllerchange',
    () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    },
    { once: true }
  );
  void navigator.serviceWorker.getRegistration().then((reg) => {
    reg?.waiting?.postMessage({ type: 'SKIP_WAITING' });
  });
}
